import { Box, Button, CircularProgress } from '@mui/material'
import { ReactElement, Suspense, lazy, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { useNotifications } from 'src/v3/hooks/use-notifications'
import { useScrollToTop } from 'src/v3/utilities/hooks/use-scroll-to-top'
import { QuizIcon } from 'src/v3/core/icons'
import { usePageTour } from 'src/v3/tours/hooks/use-page-tour'
import {
  fetchQuizLibrary,
  fetchLinkedServices,
  selectQuizLibrary,
  selectQuizLibraryStatus,
  selectLinkedServices,
  selectLinkedServicesStatus,
  selectedLinkedServiceChanged,
} from '../create.slice'
import { deleteQuizLibraryItem, migrateQuiz } from '../utils/api'
import { CREATE_QUIZ_URLS } from '../constants'
import LibraryRowBase from '../shared/components/library-row-base'
import { useLibraryFilters } from '../shared/hooks/use-library-filters'
import LibraryPageLayout from '../shared/components/library-page-layout'

let CreateQuizModal = lazy(() => import('./components/create-quiz-modal'))

let ModalFallback = (
  <Box
    className="fixed inset-0 z-50 flex items-center justify-center"
    role="status"
    aria-label="Loading modal"
  >
    <CircularProgress aria-hidden="true" />
  </Box>
)

export default function QuizLibraryPage(): ReactElement {
  useScrollToTop()
  usePageTour('quiz-library')
  let dispatch = useAppDispatch()
  let navigate = useNavigate()
  let quizList = useAppSelector(selectQuizLibrary).filter(
    (q) => !q.isMigratable,
  )
  let quizLibraryStatus = useAppSelector(selectQuizLibraryStatus)
  let linkedServices = useAppSelector(selectLinkedServices)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)

  useAutoFetch(quizLibraryStatus, fetchQuizLibrary)
  useAutoFetch(linkedServicesStatus, fetchLinkedServices)

  let {
    linkedServiceFilter,
    courseFilter,
    search,
    setLinkedServiceFilter,
    setCourseFilter,
    setSearch,
    resetFilters,
    isFiltered,
    filteredItems: filteredQuizzes,
    distinctCourses,
  } = useLibraryFilters(quizList)

  let [createQuizOpen, setCreateQuizOpen] = useState(false)
  let [deleteQuizID, setDeleteQuizID] = useState<string | null>(null)
  let [isDeleting, setIsDeleting] = useState(false)

  let { showSuccess, showError, NotificationComponent } = useNotifications()

  let handleEdit = (id: string) => {
    navigate(CREATE_QUIZ_URLS.EDIT(id), {
      state: {
        backLabel: 'Back to Quiz Library',
        backPath: CREATE_QUIZ_URLS.LIBRARY,
      },
    })
  }

  let handleMigrate = (quizID: string) => {
    let quiz = quizList.find((q) => q.id === quizID)
    if (!quiz) return
    migrateQuiz({ contentID: quizID })
      .then(() => {
        dispatch(fetchQuizLibrary())
        showSuccess('Quiz migrated successfully')
      })
      .catch(() => showError('Failed to migrate quiz'))
  }

  let handleDeleteConfirm = () => {
    if (!deleteQuizID) return
    setIsDeleting(true)
    deleteQuizLibraryItem(deleteQuizID)
      .then(() => {
        dispatch(fetchQuizLibrary())
        showSuccess('Quiz deleted successfully')
        setDeleteQuizID(null)
      })
      .catch(() => showError('Failed to delete quiz'))
      .finally(() => setIsDeleting(false))
  }

  return (
    <>
      <LibraryPageLayout
        pageTitle="Quiz Library"
        createButton={
          <Button
            variant="contained"
            size="large"
            startIcon={<QuizIcon />}
            onClick={() => setCreateQuizOpen(true)}
          >
            Create Quiz
          </Button>
        }
        itemCountLabel={`${filteredQuizzes.length} quizzes`}
        linkedServices={linkedServices}
        filters={{
          linkedServiceFilter,
          courseFilter,
          search,
          onLinkedServiceFilterChange: (v) => {
            setLinkedServiceFilter(v)
            setCourseFilter('all')
            dispatch(selectedLinkedServiceChanged(v))
          },
          onCourseFilterChange: setCourseFilter,
          onSearchChange: setSearch,
          onResetFilters: resetFilters,
          isFiltered,
          distinctCourses,
          filteredItemCount: filteredQuizzes.length,
          totalItemCount: quizList.length,
        }}
        isLoading={quizLibraryStatus === 'pending'}
        emptyMessage="No quizzes found."
        noResultsMessage="No quizzes match your filters."
        loadingAriaLabel="Loading quizzes"
        itemsLayout="list"
        deleteDialog={{
          open: !!deleteQuizID,
          onClose: () => setDeleteQuizID(null),
          onConfirm: handleDeleteConfirm,
          title: 'Delete Quiz',
          itemName: quizList.find((q) => q.id === deleteQuizID)?.name,
          isDeleting,
        }}
      >
        {filteredQuizzes.map((quiz) => (
          <LibraryRowBase
            key={quiz.id}
            item={quiz}
            contentType="quiz"
            onEdit={() => handleEdit(quiz.id)}
            onDelete={() => setDeleteQuizID(quiz.id)}
            onMigrate={() => handleMigrate(quiz.id)}
          />
        ))}
      </LibraryPageLayout>

      {createQuizOpen && (
        <Suspense fallback={ModalFallback}>
          <CreateQuizModal
            isOpen
            onClose={() => setCreateQuizOpen(false)}
            linkedServices={linkedServices}
            defaultLinkedServiceID={
              linkedServiceFilter !== 'all' ? linkedServiceFilter : ''
            }
            onSuccess={(id) => {
              dispatch(fetchQuizLibrary())
              setCreateQuizOpen(false)
              handleEdit(id)
            }}
            onFailure={() => showError('Failed to create quiz')}
          />
        </Suspense>
      )}

      {NotificationComponent}
    </>
  )
}
